'use client';

import { useState, useMemo } from 'react';
import {
  format,
  eachMonthOfInterval,
  getDaysInMonth,
  startOfMonth,
  getDay,
  isSameDay,
  isToday,
  parseISO,
  getYear,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Birthday } from '@/lib/types';
import { cn } from '@/lib/utils';
import { BirthdayCard } from './birthday-card';

interface YearlyViewProps {
  birthdays: Birthday[];
}

export function YearlyView({ birthdays }: YearlyViewProps) {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const months = eachMonthOfInterval({
    start: new Date(currentYear, 0, 1),
    end: new Date(currentYear, 11, 31),
  });

  const birthdaysByDay = useMemo(() => {
    const map = new Map<string, Birthday[]>();
    birthdays.forEach((b) => {
      const dob = parseISO(b.dob);
      const year = getYear(dob);
      for (let y = year; y <= currentYear + 1; y++) {
         const date = new Date(y, dob.getMonth(), dob.getDate());
         const key = format(date, 'yyyy-MM-dd');
         const existing = map.get(key) || [];
         map.set(key, [...existing, b]);
      }
    });
    return map;
  }, [birthdays, currentYear]);
  
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const selectedDayBirthdays = useMemo(() => {
    if (!selectedDate) return [];
    const key = format(selectedDate, 'yyyy-MM-dd');
    return birthdaysByDay.get(key) || [];
 },[selectedDate, birthdaysByDay]);


  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Year {currentYear}</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentYear(currentYear - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentYear(new Date().getFullYear())}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentYear(currentYear + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {months.map((month) => {
              const monthStart = startOfMonth(month);
              const totalDays = getDaysInMonth(month);
              const startDayOfWeek = getDay(monthStart); // 0=Sun, 1=Mon...
              const daysInMonth = Array.from({ length: totalDays }, (_, i) => i + 1);
              const emptyDays = Array.from({ length: startDayOfWeek });

              return (
                <div key={format(month, 'MMM')} className="text-center">
                  <h3 className="font-semibold text-lg mb-2">
                    {format(month, 'MMMM')}
                  </h3>
                  <div className="grid grid-cols-7 gap-1 text-xs text-muted-foreground">
                    {weekDays.map((day) => (
                      <div key={day}>{day.charAt(0)}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1 mt-1">
                    {emptyDays.map((_, i) => (
                      <div key={`empty-${i}`}></div>
                    ))}
                    {daysInMonth.map((day) => {
                      const date = new Date(currentYear, month.getMonth(), day);
                      const dateKey = format(date, 'yyyy-MM-dd');
                      const hasBirthday = birthdaysByDay.has(dateKey);
                      const isSelected = selectedDate && isSameDay(date, selectedDate);

                      return (
                        <div
                          key={day}
                          onClick={() => setSelectedDate(date)}
                          className={cn(
                            'flex items-center justify-center h-8 w-8 rounded-full cursor-pointer hover:bg-accent',
                            isToday(date) && 'border-2 border-primary',
                            hasBirthday && 'bg-primary/20 text-primary-foreground font-bold',
                            isSelected && 'bg-accent text-accent-foreground ring-2 ring-primary'
                          )}
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <div className="space-y-4">
        <Card>
            <CardHeader>
            <CardTitle>
                {selectedDate
                ? `Birthdays on ${format(selectedDate, 'MMMM d')}`
                : 'Select a day'}
            </CardTitle>
            </CardHeader>
            <CardContent>
            {selectedDate ? (
                selectedDayBirthdays.length > 0 ? (
                <div className="space-y-4">
                    {selectedDayBirthdays.map((b) => (
                    <BirthdayCard key={b.id} birthday={b} />
                    ))}
                </div>
                ) : (
                <p className="text-sm text-muted-foreground">
                    No birthdays on this day.
                </p>
                )
            ) : (
                <p className="text-sm text-muted-foreground">
                Select a day to see birthdays.
                </p>
            )}
            </CardContent>
        </Card>
        </div>
    </div>
  );
}
